import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';

import lang from '../../../../lib/language';
import { RootState } from '../../../../lib/store';
import { OpenPosition } from '../../../../lib/store/reporting/types';
import { getCurrentBuyingPower } from '../../../../lib/store/selectors';
import TradePriceCache from '../../../../lib/store-util/TradePriceCache';
import { formatCurrency, roundUnrealizedProfit } from '../../../../lib/util/DataHelpers';
import { getLongPositions, getShortPositions, getTotalValue, getUnrealizedProfit } from '../../../../lib/util/my-account-helpers';

import styles from './CashDetail.module.scss';

type Props = {
  setPortfolioValue: (value: number) => void;
};

const CashDetails = ({ setPortfolioValue }: Props) => {
  const [investments, setInvestments] = useState<number>(0);
  const [unrealizedProfitPos, setUnrealizedProfitPos] = useState<number | null>(0);

  const openPositions = useSelector<RootState, OpenPosition[]>((state: RootState) => state.reporting.openPositions);
  const buyingPower = useSelector<RootState, number>((state: RootState) => getCurrentBuyingPower(state));

  const tradePricesCache = TradePriceCache.useCustom(data => data); // TODO: Optimize this by moving this hook to each item (TradePriceCache.use(symbol, 'ItemComponentName')). Thus only the relevant items will be rendered - not the whole Watchlist Table.

  useEffect(() => {
    if (openPositions) {
      const longPositionList = getLongPositions(openPositions);
      const shortPositionList = getShortPositions(openPositions);
      const investmentsSum = getTotalValue(openPositions);

      setInvestments(investmentsSum);
      const unrealizedProfitSum = (
        (getUnrealizedProfit(longPositionList, tradePricesCache) || 0)
        + (getUnrealizedProfit(shortPositionList, tradePricesCache) || 0));

      setPortfolioValue(investmentsSum + unrealizedProfitSum);
      setUnrealizedProfitPos(unrealizedProfitSum);
    }
  }, [openPositions, setPortfolioValue, tradePricesCache]);

  return (
    <div className={classNames(styles.cardWrapper, { [styles.cardWrapperSm]: true })}>
      <div className={styles.row}>
        <div className={styles.titleWrapper}>
          <div className={classNames(styles.indicator, styles.accentCircle)} />
          <span className={styles.cardTitle}>{lang.mobilePortfolioTitle()}</span>
        </div>
        <span>{formatCurrency(investments + (unrealizedProfitPos ?? 0))}</span>
      </div>
      <div className={classNames(styles.row, styles.investments)}>
        <span className={styles.cardSubTitle}>
          {lang.commonAccountBreakdownInvestments()}
        </span>
        <span>{formatCurrency(investments)}</span>
      </div>
      <div className={classNames(styles.row, styles.unrealizedProfit)}>
        <span className={styles.cardSubTitle}>
          {lang.commonAccountBreakdownUnrealizedProfit()}
        </span>
        <span>{roundUnrealizedProfit(unrealizedProfitPos, true)}</span>
      </div>
      <div className={styles.row}>
        <div className={styles.titleWrapper}>
          <div className={classNames(styles.indicator,
            { [styles.orangeCircle]: buyingPower })}
          />
          <span className={styles.cardTitle}>{lang.commonAccountBreakdownCashToInvest()}</span>
        </div>
        <span>{formatCurrency(buyingPower)}</span>
      </div>
    </div>
  );
};

export default CashDetails;
